import { auth } from './firebase';
import OpenAI from 'openai';
import { detectCalendarQuery, processCalendarRequest } from './assistantCalendarService';
import { detectGmailQuery, processGmailRequest } from './assistantGmailService';
import { fetchWithTimeout, handleError } from './errorHandlingService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

// Initialize OpenAI with API key from environment variables
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true, // Enable API calls from browser
  fetch: fetchWithTimeout // Use our custom fetch with timeout and error handling
});

// Use environment variable for assistant ID with a fallback
const ASSISTANT_ID = process.env.REACT_APP_ASSISTANT_ID || "asst_KfLyuAXYU3uxzUmtUTUavXLX";

// Define the calendar function for OpenAI function calling
const calendarFunction = {
  name: "get_calendar_events",
  description: "Retrieve calendar events for a company based on a user query",
  parameters: {
    type: "object",
    properties: {
      companyId: { type: "string", description: "Company ID for calendar access" },
      query: { type: "string", description: "The user's calendar query" }
    },
    required: ["companyId", "query"]
  }
};

// Define the Gmail function for OpenAI function calling
const gmailFunction = {
  name: "get_gmail_messages",
  description: "Retrieve email messages from Gmail based on a user query",
  parameters: {
    type: "object",
    properties: {
      companyId: { type: "string", description: "Company ID for Gmail access" },
      query: { type: "string", description: "The user's email query, such as 'unread emails', 'emails from X', etc." }
    },
    required: ["companyId", "query"]
  }
};

// Create a new thread
export async function createThread() {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    const thread = await openai.beta.threads.create();
    return thread.id;
  } catch (error) {
    console.error('Error creating thread:', error);
    throw error;
  }
}

// Helper function to build company context string
function getCompanyContext(companyInfo) {
  if (!companyInfo) return '';
  
  // Build company context
  const nameLine = `Company Name: ${companyInfo.name}`;
  const industryLine = `Industry: ${companyInfo.industry}`;
  const sizeLine = `Size: ${companyInfo.size}`;

  // Optional fields
  const descLine = companyInfo.description ?
    `Description: ${companyInfo.description}` : "";
  const missionLine = companyInfo.mission ?
    `Mission: ${companyInfo.mission}` : "";
    
  // Note: We've removed the document content usage here since we'll now
  // be using the per-chat context documents instead of global company documents

  return `Company Context:
${nameLine}
${industryLine}
${sizeLine}
${descLine}
${missionLine}`;
}

// Add a message to a thread
export async function addMessage(threadId, message, companyInfo = null) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // If company info exists, prepend it to the message
    let messageContent = message;
    
    // Check if the message is a calendar-related query
    let calendarResponse = null;
    if (companyInfo && detectCalendarQuery(message)) {
      try {
        // Check if company has Google Calendar connected
        if (companyInfo.googleCalendar && companyInfo.id) {
          console.log('Detected calendar query, processing...');
          calendarResponse = await processCalendarRequest(message, companyInfo.id);
          
          if (calendarResponse.success) {
            console.log('Successfully processed calendar request:', calendarResponse);
          } else {
            console.warn('Failed to process calendar request:', calendarResponse.message);
          }
        } else {
          console.log('Calendar query detected but Google Calendar is not connected');
          calendarResponse = {
            success: false,
            message: "I noticed you're asking about your calendar, but you haven't connected Google Calendar yet. You can connect it in Settings."
          };
        }
      } catch (calendarError) {
        console.error('Error handling calendar query:', calendarError);
        calendarResponse = {
          success: false,
          message: `Error processing calendar request: ${calendarError.message}`
        };
      }
    }

    // Check if the message is a Gmail-related query
    let gmailResponse = null;
    if (companyInfo && detectGmailQuery(message)) {
      try {
        // Check if company has Gmail connected
        if (companyInfo.googleGmail && companyInfo.id) {
          console.log('Detected Gmail query, processing...');
          gmailResponse = await processGmailRequest(message, companyInfo.id);
          
          if (gmailResponse.success) {
            console.log('Successfully processed Gmail request:', gmailResponse);
          } else {
            console.warn('Failed to process Gmail request:', gmailResponse.message);
          }
        } else {
          console.log('Gmail query detected but Google Gmail is not connected');
          gmailResponse = {
            success: false,
            message: "I noticed you're asking about your emails, but you haven't connected Gmail yet. You can connect it in Settings."
          };
        }
      } catch (gmailError) {
        console.error('Error handling Gmail query:', gmailError);
        gmailResponse = {
          success: false,
          message: `Error processing Gmail request: ${gmailError.message}`
        };
      }
    }

    // Retrieve document contexts from Firestore
    let documentContext = '';
    if (companyInfo && companyInfo.id) {
      try {
        console.log('Fetching document contexts from Firestore for company:', companyInfo.id);
        
        // Query documents by company ID
        const documentsRef = collection(db, 'documents');
        const q = query(documentsRef, where('companyId', '==', companyInfo.id));
        const querySnapshot = await getDocs(q);
        
        // Process query results
        if (!querySnapshot.empty) {
          // Organize documents by category
          const categorizedDocs = {};
          
          querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();
            const category = data.category || 'general';
            
            if (!categorizedDocs[category]) {
              categorizedDocs[category] = [];
            }
            
            categorizedDocs[category].push({
              id: docSnapshot.id,
              name: data.name,
              content: data.content,
            });
          });
          
          // Build context string with proper labeling
          documentContext = '\n\n--- DOCUMENT CONTEXTS ---\n\n';
          
          // Process each category
          for (const category in categorizedDocs) {
            documentContext += `## ${category.toUpperCase()} DOCUMENTS:\n`;
            
            categorizedDocs[category].forEach((doc, index) => {
              documentContext += `### Document ${index + 1}: ${doc.name}\n`;
              documentContext += `${doc.content}\n\n`;
            });
          }
          
          documentContext += '--- END DOCUMENT CONTEXTS ---\n\n';
          
          console.log(`Found ${querySnapshot.size} documents in ${Object.keys(categorizedDocs).length} categories for context`);
        } else {
          console.log('No documents found in Firestore for context');
        }
      } catch (docError) {
        console.error('Error fetching documents from Firestore:', docError);
        // Continue without document context if there's an error
      }
    }

    // Process calendar and Gmail responses first if needed
    if (companyInfo) {
      // Get company context
      const companyContext = getCompanyContext(companyInfo);
      
      // If there's a calendar response, add it as a system message
      if (calendarResponse) {
        let calendarSystemMessage = '';
        
        if (calendarResponse.success && calendarResponse.data) {
          // Format the calendar data for the assistant
          if (calendarResponse.data.type === 'level10Meeting') {
            const meeting = calendarResponse.data.meeting;
            calendarSystemMessage = `
System: The user asked about their calendar. I found the following information:

CALENDAR DATA: NEXT LEVEL 10 MEETING
Title: ${meeting.summary}
Date: ${meeting.date}
Time: ${meeting.formattedTimeRange}
Location: ${meeting.location}
${meeting.attendees ? `Attendees: ${meeting.attendees}` : ''}
${meeting.description ? `Description: ${meeting.description}` : ''}
URL: ${meeting.htmlLink}

Use this information to answer the user's question about their calendar. Present the data in a clear, organized way, focusing on the most relevant information.`;
          } else if (calendarResponse.data.type === 'upcomingEvents' || calendarResponse.data.type === 'specificEvents') {
            const events = calendarResponse.data.events;
            let eventsText = events.map((event, index) => `
Event ${index + 1}: ${event.summary}
Date: ${event.date}
Time: ${event.formattedTimeRange}
Location: ${event.location}
${event.description ? `Description: ${event.description}` : ''}`).join('\n');
            
            calendarSystemMessage = `
System: The user asked about their calendar. I found the following information:

CALENDAR DATA: ${calendarResponse.data.type === 'specificEvents' ? `${calendarResponse.data.eventType.toUpperCase()} EVENTS` : 'UPCOMING EVENTS'}
${eventsText}

Use this information to answer the user's question about their calendar. Present the data in a clear, organized way, focusing on the most relevant information.`;
          }
        } else {
          // Just pass the message from the calendar response
          calendarSystemMessage = `
System: The user asked about their calendar. ${calendarResponse.message}

Use this information to address the user's calendar query. If appropriate, suggest connecting their Google Calendar in Settings if they haven't already.`;
        }
        
        // Add the calendar info as a system message
        await openai.beta.threads.messages.create(
          threadId,
          {
            role: 'user',
            content: [
              {
                type: "text",
                text: calendarSystemMessage
              }
            ]
          }
        );
      }
      
      // If there's a Gmail response, add it as a system message
      if (gmailResponse) {
        let gmailSystemMessage = '';
        
        if (gmailResponse.success && gmailResponse.data) {
          // Format the Gmail data for the assistant based on type
          if (gmailResponse.data.type === 'unreadEmails' || 
              gmailResponse.data.type === 'recentEmails' || 
              gmailResponse.data.type === 'senderEmails' || 
              gmailResponse.data.type === 'subjectEmails' || 
              gmailResponse.data.type === 'topicEmails') {
            
            const emails = gmailResponse.data.emails;
            let emailsText = emails.map((email, index) => `
Email ${index + 1}:
From: ${email.from}
To: ${email.to}
Subject: ${email.subject}
Date: ${email.date}
${email.unread ? '(UNREAD)' : ''}
Snippet: ${email.snippet}`).join('\n\n');
            
            // Determine the title based on email type
            let emailTitle = 'EMAILS';
            if (gmailResponse.data.type === 'unreadEmails') {
              emailTitle = 'UNREAD EMAILS';
            } else if (gmailResponse.data.type === 'recentEmails') {
              emailTitle = 'RECENT EMAILS';
            } else if (gmailResponse.data.type === 'senderEmails') {
              emailTitle = `EMAILS FROM ${gmailResponse.data.sender.toUpperCase()}`;
            } else if (gmailResponse.data.type === 'subjectEmails') {
              emailTitle = `EMAILS WITH SUBJECT "${gmailResponse.data.subject.toUpperCase()}"`;
            } else if (gmailResponse.data.type === 'topicEmails') {
              emailTitle = `EMAILS ABOUT "${gmailResponse.data.topic.toUpperCase()}"`;
            }
            
            gmailSystemMessage = `
System: The user asked about their emails. I found the following information:

GMAIL DATA: ${emailTitle}
${emailsText}

Use this information to answer the user's question about their emails. Present the data in a clear, organized way, focusing on the most relevant information.`;
          }
        } else {
          // Just pass the message from the Gmail response
          gmailSystemMessage = `
System: The user asked about their emails. ${gmailResponse.message}

Use this information to address the user's email query. If appropriate, suggest connecting their Gmail in Settings if they haven't already.`;
        }
        
        // Add the Gmail info as a system message
        await openai.beta.threads.messages.create(
          threadId,
          {
            role: 'user',
            content: [
              {
                type: "text",
                text: gmailSystemMessage
              }
            ]
          }
        );
      }
    }

    // Create the message with the modified content format that includes document context
    // We now add the company and document context directly to the user's message
    if (companyInfo) {
      const companyContext = getCompanyContext(companyInfo);
      
      // Add company context directly to the user's message content
      const systemInfo = `COMPANY INFORMATION:\n${companyContext}\n\n`;
      
      // Combine the user's message with context
      if (documentContext) {
        // Include explicit instructions about using the document context
        messageContent = `${messageContent}\n\n${documentContext}\n\nIMPORTANT: Use the above document context to answer my question. These documents contain specific information about our company that should inform your response.`;
      }
    }
    
    // Create the message with the new content format
    const response = await openai.beta.threads.messages.create(
      threadId,
      {
        role: "user",
        content: [
          {
            type: "text",
            text: messageContent
          }
        ]
      }
    );
    
    return response;
  } catch (error) {
    // Use our custom error handling for better UX
    handleError(error, 'Failed to send your message');
    throw error;
  }
}

// Run the assistant on a thread
export async function runAssistant(threadId, stream = false) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    const run = await openai.beta.threads.runs.create(
      threadId,
      {
        assistant_id: ASSISTANT_ID,
        stream: stream
      }
    );
    
    return run.id;
  } catch (error) {
    console.error('Error running assistant:', error);
    handleError(error, 'Failed to run the assistant');
    throw error;
  }
}

// Check the status of a run
export async function checkRunStatus(threadId, runId) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    const run = await openai.beta.threads.runs.retrieve(
      threadId,
      runId
    );
    
    return run.status;
  } catch (error) {
    console.error('Error checking run status:', error);
    throw error;
  }
}

// Retrieve messages from a thread
export async function getMessages(threadId) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    const messages = await openai.beta.threads.messages.list(threadId);
    return messages.data;
  } catch (error) {
    console.error('Error retrieving messages:', error);
    throw error;
  }
}

// Complete function to send message and get response
export async function sendMessageAndWaitForResponse(threadId, message, companyInfo = null) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Add the message to the thread
    await addMessage(threadId, message, companyInfo);
    
    // Run the assistant
    const runId = await runAssistant(threadId);
    
    // Wait for completion
    const response = await waitForCompletion(threadId, runId);
    
    return response;
  } catch (error) {
    console.error('Error in message flow:', error);
    throw error;
  }
}

// Poll for run completion and return messages when done
export async function waitForCompletion(threadId, runId, maxAttempts = 30) {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        // Ensure user is authenticated
        if (!auth.currentUser) {
          reject(new Error('User must be authenticated'));
          return;
        }
        
        const run = await openai.beta.threads.runs.retrieve(threadId, runId);
        
        if (run.status === 'requires_action' && run.required_action?.type === 'submit_tool_outputs') {
          console.log('Function call detected:', run.required_action.submit_tool_outputs);
          
          try {
            // Process tool calls and get outputs
            const toolOutputs = await handleToolCalls(
              run.required_action.submit_tool_outputs.tool_calls,
              threadId
            );
            
            // Submit tool outputs back to the run
            await openai.beta.threads.runs.submitToolOutputs(
              threadId,
              runId,
              { tool_outputs: toolOutputs }
            );
            
            // Continue polling
            setTimeout(checkStatus, 1000);
          } catch (toolError) {
            console.error('Error handling tool calls:', toolError);
            reject(toolError);
          }
          return;
        }
        
        if (run.status === 'completed') {
          const messages = await getMessages(threadId);
          resolve(messages);
          return;
        }
        
        if (run.status === 'failed' || run.status === 'cancelled' || run.status === 'expired') {
          reject(new Error(`Run failed with status: ${run.status}`));
          return;
        }
        
        // Keep polling if still in progress
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 1000); // Check every second
        } else {
          reject(new Error('Max polling attempts reached'));
        }
      } catch (error) {
        reject(error);
      }
    };
    
    checkStatus();
  });
}

// Handle tool calls from the assistant
async function handleToolCalls(toolCalls, threadId) {
  const toolOutputs = [];
  
  for (const toolCall of toolCalls) {
    if (toolCall.type === 'code_interpreter') {
      try {
        // Log that code interpreter is being used
        console.log('Code interpreter tool call detected:', toolCall.id);
        
        // For code interpreter, we don't need to process anything here
        // The code is executed in the OpenAI environment
        // We'll just acknowledge it was used
        toolOutputs.push({
          tool_call_id: toolCall.id,
          output: JSON.stringify({
            success: true,
            message: "Code interpreter executed successfully."
          })
        });
      } catch (error) {
        console.error('Error handling code interpreter:', error);
        toolOutputs.push({
          tool_call_id: toolCall.id,
          output: JSON.stringify({
            success: false,
            message: `Error: ${error.message}`
          })
        });
      }
    } else if (toolCall.function && toolCall.function.name === 'get_calendar_events') {
      try {
        // Parse the function arguments
        const args = JSON.parse(toolCall.function.arguments);
        console.log('Calendar function called with args:', args);
        
        const { companyId, query } = args;
        
        if (!companyId) {
          toolOutputs.push({
            tool_call_id: toolCall.id,
            output: JSON.stringify({
              success: false,
              message: "Calendar is not connected. Please connect your Google Calendar in Settings."
            })
          });
          continue;
        }
        
        console.log('Connecting to calendar service and retrieving data...');
        
        // Process the calendar request using our existing function
        const calendarResponse = await processCalendarRequest(query, companyId);
        
        // Log success or failure
        if (calendarResponse.success) {
          console.log('Successfully retrieved calendar data:', 
            calendarResponse.data ? calendarResponse.data.type : 'No events found');
        } else {
          console.warn('Failed to retrieve calendar data:', calendarResponse.message);
        }
        
        toolOutputs.push({
          tool_call_id: toolCall.id,
          output: JSON.stringify(calendarResponse)
        });
      } catch (error) {
        console.error('Error processing calendar function call:', error);
        toolOutputs.push({
          tool_call_id: toolCall.id,
          output: JSON.stringify({
            success: false,
            message: `Error: ${error.message}`
          })
        });
      }
    } else if (toolCall.function && toolCall.function.name === 'get_gmail_messages') {
      try {
        // Parse the function arguments
        const args = JSON.parse(toolCall.function.arguments);
        console.log('Gmail function called with args:', args);
        
        const { companyId, query } = args;
        
        if (!companyId) {
          toolOutputs.push({
            tool_call_id: toolCall.id,
            output: JSON.stringify({
              success: false,
              message: "Gmail is not connected. Please connect your Gmail account in Settings."
            })
          });
          continue;
        }
        
        console.log('Connecting to Gmail service and retrieving messages...');
        
        // Process the Gmail request using our existing function
        const gmailResponse = await processGmailRequest(query, companyId);
        
        // Log success or failure
        if (gmailResponse.success) {
          console.log('Successfully retrieved Gmail data:', 
            gmailResponse.data ? gmailResponse.data.type : 'No emails found');
        } else {
          console.warn('Failed to retrieve Gmail data:', gmailResponse.message);
        }
        
        toolOutputs.push({
          tool_call_id: toolCall.id,
          output: JSON.stringify(gmailResponse)
        });
      } catch (error) {
        console.error('Error processing Gmail function call:', error);
        toolOutputs.push({
          tool_call_id: toolCall.id,
          output: JSON.stringify({
            success: false,
            message: `Error: ${error.message}`
          })
        });
      }
    } else {
      // Handle unknown function
      toolOutputs.push({
        tool_call_id: toolCall.id,
        output: JSON.stringify({
          success: false,
          message: `Function ${toolCall.function?.name || 'unknown'} is not implemented`
        })
      });
    }
  }
  
  return toolOutputs;
}

// Run the assistant with real streaming using SSE
export async function runAssistantWithRealStreaming(threadId, onUpdate) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Starting streaming run for thread:', threadId);
    
    // Initialize with a thinking state
    onUpdate({
      role: 'assistant',
      content: [{ 
        type: 'text', 
        text: { value: 'Thinking...' } 
      }],
      timestamp: new Date()
    }, false);
    
    // Create a streaming run with proper event handling
    const stream = await openai.beta.threads.runs.stream(
      threadId,
      { assistant_id: ASSISTANT_ID }
    );
    
    console.log('Stream connected');
    
    let currentText = "";
    let messageId = null;
    
    // Handle stream connection
    stream.on('connect', () => {
      console.log('Stream connection established');
    });
    
    // Handle errors in the stream
    stream.on('error', (error) => {
      console.error('Stream error:', error);
      onUpdate({
        role: 'assistant',
        content: [{ 
          type: 'text', 
          text: { value: `Error: ${error.message || 'Connection error'}` } 
        }],
        timestamp: new Date()
      }, true); // Mark as final with error
    });

    // Handle text creation event
    stream.on('textCreated', (text) => {
      console.log('Text created with ID:', text.id);
      messageId = text.id;
      currentText = ""; // Reset current text
    });
    
    // Handle text delta events for real-time typing effect
    stream.on('textDelta', (delta, snapshot) => {
      // Use snapshot value directly for more reliable text updates
      currentText = snapshot.value;
      console.log('Text delta received, current length:', currentText.length);
      
      // Update UI with each text delta for typing animation effect
      onUpdate({
        id: messageId,
        role: 'assistant',
        content: [{ 
          type: 'text', 
          text: { value: currentText } 
        }],
        timestamp: new Date(),
        isStreaming: true
      }, false);
    });

    // Handle tool call creation including code interpreter
    stream.on('toolCallCreated', (toolCall) => {
      console.log('Tool call created:', toolCall.id, toolCall.type);
      
      // Different message based on tool type
      if (toolCall.type === 'code_interpreter') {
        onUpdate({
          id: toolCall.id,
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: 'Working with code interpreter to solve your problem...' } 
          }],
          timestamp: new Date(),
          isStreaming: true,
          toolType: 'code_interpreter'
        }, false);
      } else if (toolCall.type === 'function' && toolCall.function?.name) {
        let userMessage = `Using function: ${toolCall.function.name}...`;
        
        // Custom messages for specific tools
        if (toolCall.function.name === 'get_calendar_events') {
          userMessage = 'Connecting to your calendar to retrieve event information...';
        } else if (toolCall.function.name === 'get_gmail_messages') {
          userMessage = 'Connecting to your Gmail account to retrieve email information...';
        }
        
        onUpdate({
          id: toolCall.id,
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: userMessage } 
          }],
          timestamp: new Date(),
          isStreaming: true
        }, false);
      }
    });

    // Handle tool call deltas - IMPORTANT for proper tool handling
    stream.on('toolCallDelta', (delta, snapshot) => {
      console.log('Tool call delta received for:', snapshot.type);
      
      // For code interpreter, show updates about code execution
      if (delta.type === 'code_interpreter') {
        if (delta.code_interpreter && delta.code_interpreter.input) {
          console.log('Code interpreter executing code:', delta.code_interpreter.input);
          onUpdate({
            id: snapshot.id,
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: 'Executing code to solve your problem...' } 
            }],
            timestamp: new Date(),
            isStreaming: true,
            toolType: 'code_interpreter'
          }, false);
        }
        
        // Handle code interpreter outputs, especially file outputs
        if (delta.code_interpreter && delta.code_interpreter.outputs) {
          console.log('Code interpreter outputs received:', JSON.stringify(delta.code_interpreter.outputs));
          
          delta.code_interpreter.outputs.forEach(output => {
            console.log('Code interpreter output type:', output.type);
            
            if (output.type === 'logs') {
              console.log('Code execution logs:', output.logs);
            } else if (output.type === 'image') {
              console.log('Code generated image file ID:', output.image.file_id);
              // Store the file_id for later retrieval and capture it in the message
              const fileId = output.image.file_id;
              onUpdate({
                id: snapshot.id,
                role: 'assistant',
                content: [{ 
                  type: 'text', 
                  text: { value: `Generated image with code interpreter (file ID: ${fileId})` } 
                }],
                timestamp: new Date(),
                isStreaming: true,
                toolType: 'code_interpreter',
                fileId: fileId,
                fileIds: [fileId] // Add to array of fileIds for better tracking
              }, false);
            } else if (output.type === 'file') {
              console.log('Code generated file ID:', output.file.file_id);
              const fileId = output.file.file_id;
              onUpdate({
                id: snapshot.id,
                role: 'assistant',
                content: [{ 
                  type: 'text', 
                  text: { value: `Generated file with code interpreter (file ID: ${fileId})` } 
                }],
                timestamp: new Date(),
                isStreaming: true,
                toolType: 'code_interpreter',
                fileId: fileId,
                fileIds: [fileId] // Add to array of fileIds for better tracking
              }, false);
            }
          });
        }
      } else if (delta.type === 'function' && delta.function?.arguments) {
        // For function progress updates (optional)
        console.log('Function arguments updated:', delta.function.arguments);
      }
    });
    
    // Handle message creation
    stream.on('messageCreated', (message) => {
      console.log('Message created:', message.id);
      if (!messageId) {
        messageId = message.id;
      }
    });
    
    // Handle message deltas
    stream.on('messageDelta', (delta, snapshot) => {
      console.log('Message delta received:', JSON.stringify(delta));
      
      if (delta.content && delta.content.length > 0) {
        for (const content of delta.content) {
          if (content.type === 'text' && content.text && content.text.value) {
            // Only update text if we're not already receiving text deltas
            if (!currentText) {
              currentText = content.text.value;
            } else {
              currentText += content.text.value;
            }
            
            console.log('Message delta received, current length:', currentText.length);
            
            // Update UI with each text delta
            onUpdate({
              id: messageId || snapshot.id,
              role: 'assistant',
              content: [{ 
                type: 'text', 
                text: { value: currentText } 
              }],
              timestamp: new Date(),
              isStreaming: true
            }, false);
          } else if (content.type === 'image_file' && content.image_file && content.image_file.file_id) {
            // Handle image files generated by code interpreter
            const fileId = content.image_file.file_id;
            console.log('Image file received in messageDelta:', fileId);
            
            // Update the message to include the image file ID
            onUpdate({
              id: messageId || snapshot.id,
              role: 'assistant',
              content: [
                { 
                  type: 'text', 
                  text: { value: currentText || 'Here is the generated image:' } 
                },
                {
                  type: 'image_file',
                  image_file: { file_id: fileId }
                }
              ],
              timestamp: new Date(),
              isStreaming: true,
              fileId: fileId,
              toolType: 'code_interpreter'
            }, false);
          } else if (content.type === 'file_attachment' && content.file_attachment && content.file_attachment.file_id) {
            // Handle other file attachments generated by code interpreter
            const fileId = content.file_attachment.file_id;
            console.log('File attachment received in messageDelta:', fileId);
            
            onUpdate({
              id: messageId || snapshot.id,
              role: 'assistant',
              content: [
                { 
                  type: 'text', 
                  text: { value: currentText || 'Here is the generated file:' } 
                },
                {
                  type: 'file_attachment',
                  file_attachment: { file_id: fileId }
                }
              ],
              timestamp: new Date(),
              isStreaming: true,
              fileId: fileId,
              toolType: 'code_interpreter'
            }, false);
          }
        }
      }
      
      // Handle annotations for file paths and citations in text
      if (delta.content && delta.content.length > 0) {
        const textItems = delta.content.filter(item => 
          item.type === 'text' && item.text && item.text.annotations
        );
        
        for (const textItem of textItems) {
          if (textItem.text.annotations && textItem.text.annotations.length > 0) {
            console.log('Found annotations in message delta:', textItem.text.annotations);
            
            for (const annotation of textItem.text.annotations) {
              if (annotation.type === 'file_path' && annotation.file_path && annotation.file_path.file_id) {
                const fileId = annotation.file_path.file_id;
                console.log('File path annotation with file ID:', fileId);
                
                // Update with the file ID information
                onUpdate({
                  id: messageId || snapshot.id,
                  role: 'assistant',
                  content: [{ 
                    type: 'text', 
                    text: { value: currentText }
                  }],
                  timestamp: new Date(),
                  isStreaming: true,
                  fileId: fileId, // Save the file ID for later use
                  toolType: 'code_interpreter',
                  hasFileAnnotation: true
                }, false);
              }
            }
          }
        }
      }
    });
    
    // Handle tool calls requiring action
    stream.on('requiresAction', async (requiresAction) => {
      console.log('Function call requires action:', requiresAction);
      
      if (requiresAction.submit_tool_outputs) {
        try {
          // Process tool calls
          const toolOutputs = await handleToolCalls(
            requiresAction.submit_tool_outputs.tool_calls,
            threadId
          );
          
          // Check for any code interpreter calls
          const hasCodeInterpreter = requiresAction.submit_tool_outputs.tool_calls.some(
            call => call.type === 'code_interpreter'
          );
          
          // Check function types before submitting outputs
          const hasCalendarCall = requiresAction.submit_tool_outputs.tool_calls.some(
            call => call.function?.name === 'get_calendar_events'
          );
          
          const hasGmailCall = requiresAction.submit_tool_outputs.tool_calls.some(
            call => call.function?.name === 'get_gmail_messages'
          );
          
          // Update UI with processing state
          let statusMessage = 'Processing data...';
          let toolType = null;
          
          if (hasCodeInterpreter) {
            statusMessage = 'Code interpreter is analyzing your data...';
            toolType = 'code_interpreter';
          } else if (hasCalendarCall && hasGmailCall) {
            statusMessage = 'Calendar and email data retrieved, analyzing your information...';
            toolType = 'calendar+gmail';
          } else if (hasCalendarCall) {
            statusMessage = 'Calendar data retrieved, analyzing your schedule information...';
            toolType = 'calendar';
          } else if (hasGmailCall) {
            statusMessage = 'Email data retrieved, analyzing your messages...';
            toolType = 'gmail';
          }
          
          // Create message with appropriate tool flags
          const statusUpdateMessage = {
            id: messageId || 'function-result',
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: statusMessage } 
            }],
            timestamp: new Date(),
            isStreaming: true,
            toolType: toolType
          };
          
          // Add the calendarToolUsed flag when calendar data is accessed
          if (hasCalendarCall) {
            statusUpdateMessage.calendarToolUsed = true;
          }
          
          // Update the UI with the status and tool flags
          onUpdate(statusUpdateMessage, false);
          
          // Submit tool outputs back to continue the stream
          // This is the critical part that was causing the stream to stop
          await stream.submitToolOutputs({ tool_outputs: toolOutputs });
          console.log('Tool outputs submitted, stream should continue');
          
        } catch (error) {
          console.error('Error handling streaming tool calls:', error);
          
          // Update UI with error
          onUpdate({
            id: messageId || 'function-error',
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: `Error processing request: ${error.message}` } 
            }],
            timestamp: new Date(),
            isStreaming: false
          }, true);
        }
      }
    });
    
    // Handle run completion
    stream.on('runDone', (run) => {
      console.log('Run status updated:', run.status);
      
      if (run.status === 'completed') {
        // Final update with completed status
        if (currentText) {
          console.log('Run completed with content, sending final update');
          
          // Create final message with all the needed properties
          const finalMessage = {
            id: messageId,
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: currentText } 
            }],
            timestamp: new Date(),
            isStreaming: false
          };
          
          // Preserve the tool type if it was set during streaming
          // Instead of DOM query (which doesn't work on server), check if we had any calendar calls
          // We can track this by checking if any tool calls were made for calendar functions
          // This is a simplified approach - in a real environment you'd have a better state tracking mechanism
          const hadCalendarCalls = true; // We're assuming calendar was used since we can't access previous state here
          
          // Check the current message content for calendar-related terms as a fallback
          const hasCalendarContent = currentText.toLowerCase().includes('calendar') || 
                                     currentText.toLowerCase().includes('schedule') || 
                                     currentText.toLowerCase().includes('meeting') ||
                                     currentText.toLowerCase().includes('event');
          
          // If we know calendar was accessed or the content suggests it was, set the appropriate flags
          if (hasCalendarContent) {
            finalMessage.toolType = 'calendar';
            finalMessage.calendarToolUsed = true;
          }
          
          // Send the final update with all necessary flags
          onUpdate(finalMessage, true); // CRITICAL: Mark as final=true to trigger Firestore save
        } else {
          // If no content was streamed, fetch the messages as fallback
          console.log('No streamed content, fetching messages as fallback');
          getMessages(threadId).then(messages => {
            const assistantMessages = messages.filter(msg => msg.role === 'assistant');
            if (assistantMessages.length > 0) {
              console.log('Retrieved final message:', JSON.stringify(assistantMessages[0]));
              
              // Process all content items to find file outputs
              const hasFileOutputs = assistantMessages[0].content && 
                assistantMessages[0].content.some(item => 
                  item.type === 'image_file' || 
                  item.type === 'file_attachment' ||
                  (item.type === 'text' && item.text && item.text.annotations && 
                   item.text.annotations.some(ann => ann.type === 'file_path'))
                );
              
              // Find any file IDs within the content
              let fileIds = [];
              if (assistantMessages[0].content) {
                for (const item of assistantMessages[0].content) {
                  if (item.type === 'image_file' && item.image_file) {
                    const fileId = item.image_file.file_id;
                    fileIds.push(fileId);
                    console.log('Found image file ID in final message:', fileId);
                  } else if (item.type === 'file_attachment' && item.file_attachment) {
                    const fileId = item.file_attachment.file_id;
                    fileIds.push(fileId);
                    console.log('Found file attachment ID in final message:', fileId);
                  } else if (item.type === 'text' && item.text && item.text.annotations) {
                    const filePathAnnotations = item.text.annotations.filter(
                      ann => ann.type === 'file_path' && ann.file_path
                    );
                    for (const annotation of filePathAnnotations) {
                      const fileId = annotation.file_path.file_id;
                      fileIds.push(fileId);
                      console.log('Found file path annotation ID in final message:', fileId);
                    }
                  }
                }
              }
              
              // Also check direct file_ids array if it exists
              if (assistantMessages[0].file_ids && assistantMessages[0].file_ids.length > 0) {
                fileIds = [...fileIds, ...assistantMessages[0].file_ids];
                console.log('Found file_ids in message:', assistantMessages[0].file_ids);
              }
              
              if (hasFileOutputs) {
                console.log('Message contains file outputs from code interpreter');
              }
              
              // Add the fileIds to the message if found
              const finalMessage = {...assistantMessages[0]};
              if (fileIds.length > 0) {
                finalMessage.fileIds = fileIds;
                finalMessage.fileId = fileIds[0]; // For backward compatibility
                finalMessage.toolType = 'code_interpreter';
              }
              
              // Check if we need to add calendarToolUsed flag
              // Look through content to see if calendar data was accessed
              const hasCalendarContent = finalMessage.content && 
                finalMessage.content.some(item => 
                  item.type === 'text' && 
                  item.text && 
                  item.text.value && 
                  item.text.value.toLowerCase().includes('calendar')
                );
                
              // Set calendarToolUsed flag for calendar-related tool types
              if (finalMessage.toolType === 'calendar' || 
                  finalMessage.toolType === 'calendar+gmail' || 
                  hasCalendarContent) {
                finalMessage.calendarToolUsed = true;
              }
              
              onUpdate(finalMessage, true); // Mark as final=true
            } else {
              onUpdate({
                role: 'assistant',
                content: [{ 
                  type: 'text', 
                  text: { value: 'The assistant did not provide a response. Please try again.' } 
                }],
                timestamp: new Date()
              }, true); // Mark as final=true
            }
          }).catch(error => {
            console.error('Error fetching messages after run completion:', error);
            // Ensure we still mark the message as final even on error
            onUpdate({
              role: 'assistant',
              content: [{ 
                type: 'text', 
                text: { value: `Error fetching response: ${error.message}` } 
              }],
              timestamp: new Date(),
              isStreaming: false
            }, true); // Mark as final=true
          });
        }
      } else if (run.status === 'failed') {
        console.error('Run failed:', run.error);
        onUpdate({
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: `Error: ${run.error?.message || 'The assistant encountered an error.'}` } 
          }],
          timestamp: new Date(),
          isStreaming: false
        }, true); // Mark as final=true
      }
    });
    
    // Handle stream end
    stream.on('end', () => {
      console.log('Stream ended');
      // No need for additional processing here as runDone should have handled it
      // If runDone hasn't fired for some reason, we'll ensure final state
      setTimeout(() => {
        if (currentText) {
          console.log('Stream ended with content, ensuring final update is sent');
          onUpdate({
            id: messageId,
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: currentText } 
            }],
            timestamp: new Date(),
            isStreaming: false
          }, true); // Mark as final=true
        }
      }, 500);
    });
    
    try {
      // Wait for the stream to complete
      await stream.done();
      console.log('Stream completed with done() call');
    } catch (streamError) {
      console.error('Error in stream.done():', streamError);
    }
    
    return messageId;
  } catch (error) {
    console.error('Error in streaming:', error);
    onUpdate({
      role: 'assistant',
      content: [{ 
        type: 'text', 
        text: { value: `Error: ${error.message}` } 
      }],
      timestamp: new Date(),
      isStreaming: false
    }, true); // Mark as final=true
    throw error;
  }
}

// Send message with real-time streaming response
export async function sendMessageWithRealStreaming(threadId, message, companyInfo = null, onUpdate) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Adding message to thread before streaming');

    // Log document context information if available
    if (companyInfo && companyInfo.id) {
      try {
        // Query documents by company ID
        const documentsRef = collection(db, 'documents');
        const q = query(documentsRef, where('companyId', '==', companyInfo.id));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const categories = {};
          querySnapshot.forEach(doc => {
            const data = doc.data();
            const category = data.category || 'general';
            categories[category] = (categories[category] || 0) + 1;
          });
          
          console.log(`Including document context: ${querySnapshot.size} total documents in categories:`, 
            Object.entries(categories).map(([category, count]) => `${category}(${count})`).join(', '));
        }
      } catch (err) {
        console.error('Error checking document context before sending message:', err);
      }
    }
    
    // Use our safe API call wrapper
    await safeApiCall(
      () => addMessage(threadId, message, companyInfo),
      'Failed to send your message'
    );
    
    return await runAssistantWithRealStreaming(threadId, onUpdate);
  } catch (error) {
    // Provide user feedback when streaming fails
    if (onUpdate) {
      onUpdate({
        error: true,
        message: 'Failed to communicate with the assistant. Please try again later.'
      }, true);
    }
    
    handleError(error, 'Failed to communicate with the assistant');
    throw error;
  }
}

// Download a file generated by Code Interpreter
export async function downloadFileContent(fileId) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Downloading file content for file ID:', fileId);
    
    // Get file content from OpenAI API
    const fileContent = await openai.files.content(fileId);
    
    // Convert to array buffer for browser download
    const arrayBuffer = await fileContent.arrayBuffer();
    
    // Get file metadata to determine file type and name
    const fileMetadata = await openai.files.retrieve(fileId);
    
    // Create a default filename based on ID and guess extension from purpose/filename if available
    let filename = fileId;
    if (fileMetadata.filename) {
      filename = fileMetadata.filename;
    } else {
      // Try to determine file extension from content type
      if (fileMetadata.content_type) {
        const extensionMap = {
          'image/png': '.png',
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'text/csv': '.csv',
          'application/json': '.json',
          'text/plain': '.txt',
          'application/pdf': '.pdf',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx'
        };
        const extension = extensionMap[fileMetadata.content_type] || '';
        if (!filename.endsWith(extension)) {
          filename = `${filename}${extension}`;
        }
      }
    }
    
    console.log('File metadata:', fileMetadata);
    
    return {
      arrayBuffer,
      filename,
      contentType: fileMetadata.content_type || 'application/octet-stream'
    };
  } catch (error) {
    console.error('Error downloading file:', error);
    handleError(error, 'Failed to download file');
    throw error;
  }
}

// Helper function to initiate browser download of a file
export function saveFileToDevice(arrayBuffer, filename, contentType) {
  try {
    // Create a blob from the array buffer
    const blob = new Blob([arrayBuffer], { type: contentType });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    // Append to document, trigger click, and remove
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Error saving file to device:', error);
    return false;
  }
}

// Legacy streaming function (simulation)
export async function runAssistantStreaming(threadId, onUpdate) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Run the assistant
    const runId = await runAssistant(threadId);
    
    let pollCount = 0;
    const maxPolls = 60; // Prevent infinite polling
    
    // Poll for status updates
    const pollInterval = setInterval(async () => {
      try {
        pollCount++;
        if (pollCount > maxPolls) {
          clearInterval(pollInterval);
          onUpdate({
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: 'Error: Response timeout exceeded. Please try again.' } 
            }],
            timestamp: new Date()
          }, true);
          return;
        }
        
        // Ensure user is still authenticated for each poll
        if (!auth.currentUser) {
          clearInterval(pollInterval);
          onUpdate({
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: 'Error: Authentication lost during processing. Please log in again.' } 
            }],
            timestamp: new Date()
          }, true);
          return;
        }
        
        const status = await checkRunStatus(threadId, runId);
        
        if (status === 'completed') {
          clearInterval(pollInterval);
          
          // Get the latest messages
          const messages = await getMessages(threadId);
          
          // Find the most recent assistant message
          const assistantMessages = messages.filter(msg => msg.role === 'assistant');
          if (assistantMessages.length > 0) {
            onUpdate(assistantMessages[0], true); // true indicates final message
          } else {
            onUpdate({
              role: 'assistant',
              content: [{ 
                type: 'text', 
                text: { value: 'No response was generated. Please try again.' } 
              }],
              timestamp: new Date()
            }, true);
          }
        } else if (status === 'failed' || status === 'cancelled' || status === 'expired') {
          clearInterval(pollInterval);
          onUpdate({
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: `Error: The assistant encountered an error (${status}). Please try again.` } 
            }],
            timestamp: new Date()
          }, true);
        } else if (pollCount % 3 === 0) {
          // Every 3 seconds, show a "thinking" indicator
          onUpdate({
            role: 'assistant',
            content: [{ 
              type: 'text', 
              text: { value: `Thinking${'.'.repeat(Math.min(3, Math.floor(pollCount/3)))}` } 
            }],
            timestamp: new Date()
          }, false); // false indicates this is not the final message
        }
      } catch (error) {
        clearInterval(pollInterval);
        console.error('Error in polling:', error);
        onUpdate({
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: `Error: ${error.message}` } 
          }],
          timestamp: new Date()
        }, true);
      }
    }, 1000);
    
    return runId;
  } catch (error) {
    console.error('Error in streaming:', error);
    onUpdate({
      role: 'assistant',
      content: [{ 
        type: 'text', 
        text: { value: `Error: ${error.message}` } 
      }],
      timestamp: new Date()
    }, true);
    throw error;
  }
}

// Legacy - Send message with streaming simulation
export async function sendMessageWithStreaming(threadId, message, companyInfo = null, onUpdate) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Add the message to the thread
    await addMessage(threadId, message, companyInfo);
    
    // Run the assistant with streaming
    return runAssistantStreaming(threadId, onUpdate);
  } catch (error) {
    console.error('Error in streaming message flow:', error);
    onUpdate({
      role: 'assistant',
      content: [{ 
        type: 'text', 
        text: { value: `Error: ${error.message}` } 
      }],
      timestamp: new Date()
    }, true);
    throw error;
  }
}

// Generate AI title for a chat
export async function generateChatTitle(threadId, firstMessage) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Create a temporary thread for title generation to avoid affecting the main thread
    const titleThreadId = await createThread();
    
    // More robust system instructions with strict formatting requirements
    const systemInstruction = `
      System: You are a precise, accurate chat title generator. Your task is to create short, descriptive titles based on the user's first message.
      
      INSTRUCTIONS:
      1. Generate ONLY a title with a maximum of 5 words.
      2. The title should be clear, concise, and directly related to the main topic or question.
      3. Do NOT use quotes or special characters in your response.
      4. Avoid using generic titles like "Chat" or "Conversation".
      5. Do NOT explain your reasoning or provide any additional text.
      6. ONLY respond with the title text - nothing else.
      7. If the message is a greeting or too vague, use "New Conversation" as the title.
      8. Format: Simple text with title case capitalization (e.g., "Project Planning Discussion").
      
      For example:
      Message: "How do I optimize my website for SEO?"
      Correct response: Website SEO Optimization
      
      Message: "Can you help me with Python programming?"
      Correct response: Python Programming Help
    `;
    
    // Add system instructions first
    await addMessage(titleThreadId, systemInstruction);
    
    // Add the user's message for title generation
    const titlePrompt = `Generate a title for this message: "${firstMessage.substring(0, 250)}${firstMessage.length > 250 ? '...' : ''}"`;
    
    await addMessage(titleThreadId, titlePrompt);
    
    // Run the assistant on the temporary thread
    const runId = await runAssistant(titleThreadId);
    
    // Wait for the completion
    const response = await waitForCompletion(titleThreadId, runId);
    
    // Extract the title from the response
    const assistantMessage = response.find(msg => msg.role === 'assistant');
    if (!assistantMessage || !assistantMessage.content || assistantMessage.content.length === 0) {
      throw new Error('No title generated');
    }
    
    // Extract the text from the content
    const content = assistantMessage.content[0];
    let title = '';
    
    if (content.type === 'text') {
      title = content.text.value;
    }
    
    // Clean up the title - remove quotes, trim, and limit length
    title = title.replace(/["'`]/g, '').trim();
    
    // Validate title format and length
    if (!title || title.length === 0) {
      throw new Error('Empty title generated');
    }
    
    // If title is longer than expected, truncate it
    if (title.split(' ').length > 8 || title.length > 40) {
      const words = title.split(' ').slice(0, 8);
      title = words.join(' ');
      if (title.length > 40) {
        title = title.substring(0, 40) + '...';
      }
    }
    
    console.log('AI generated title:', title);
    return title;
  } catch (error) {
    console.error('Error generating chat title:', error);
    // Return a fallback title based on the first message
    let fallbackTitle;
    
    if (!firstMessage || firstMessage.trim().length === 0) {
      fallbackTitle = 'New Conversation';
    } else if (firstMessage.length <= 30) {
      fallbackTitle = firstMessage.trim();
    } else {
      // Extract first sentence or first few words
      const firstSentence = firstMessage.split(/[.!?]/)[0].trim();
      if (firstSentence.length <= 30) {
        fallbackTitle = firstSentence;
      } else {
        // Get first 5 words
        const words = firstMessage.split(' ').slice(0, 5);
        fallbackTitle = words.join(' ') + '...';
      }
    }
    
    return fallbackTitle;
  }
}

// Upload a file to OpenAI
export async function uploadFile(file) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Uploading file to OpenAI:', file.name);
    
    // Create a form data object to upload the file
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', 'assistants');
    
    // Upload the file to OpenAI
    const uploadedFile = await openai.files.create({
      file: file,
      purpose: 'assistants',
    });
    
    console.log('File uploaded successfully:', uploadedFile.id);
    
    return uploadedFile;
  } catch (error) {
    console.error('Error uploading file to OpenAI:', error);
    throw error;
  }
}

// Upload multiple files
export async function uploadFiles(files) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Upload each file and return array of results
    const uploadPromises = files.map(file => uploadFile(file));
    const uploadedFiles = await Promise.all(uploadPromises);
    
    return uploadedFiles;
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    throw error;
  }
}

// Add a message with files to a thread
export async function addMessageWithFiles(threadId, messageContent, files, companyInfo = null) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    // Upload files first
    const uploadedFiles = await uploadFiles(files);
    console.log('Files uploaded:', uploadedFiles.map(file => file.id));
    
    // Step 1: Create message with files reference
    const fileIds = uploadedFiles.map(file => file.id);
    
    // Structure the message content array
    const content = [];
    
    // Add the text message with file reference
    let fullMessageContent = messageContent;
    if (fileIds.length > 0) {
      fullMessageContent = `${messageContent}\n\nI've uploaded ${fileIds.length} file(s) for you to analyze. Please review the content of these files.`;
    }
    
    // Retrieve document contexts from Firestore 
    let documentContext = '';
    if (companyInfo && companyInfo.id) {
      try {
        console.log('Fetching document contexts from Firestore for file upload:', companyInfo.id);
        
        // Query documents by company ID
        const documentsRef = collection(db, 'documents');
        const q = query(documentsRef, where('companyId', '==', companyInfo.id));
        const querySnapshot = await getDocs(q);
        
        // Process query results
        if (!querySnapshot.empty) {
          // Organize documents by category
          const categorizedDocs = {};
          
          querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();
            const category = data.category || 'general';
            
            if (!categorizedDocs[category]) {
              categorizedDocs[category] = [];
            }
            
            categorizedDocs[category].push({
              id: docSnapshot.id,
              name: data.name,
              content: data.content,
            });
          });
          
          // Build context string with proper labeling
          documentContext = '\n\n--- DOCUMENT CONTEXTS ---\n\n';
          
          // Process each category
          for (const category in categorizedDocs) {
            documentContext += `## ${category.toUpperCase()} DOCUMENTS:\n`;
            
            categorizedDocs[category].forEach((doc, index) => {
              documentContext += `### Document ${index + 1}: ${doc.name}\n`;
              documentContext += `${doc.content}\n\n`;
            });
          }
          
          documentContext += '--- END DOCUMENT CONTEXTS ---\n\n';
          
          console.log(`Found ${querySnapshot.size} documents in ${Object.keys(categorizedDocs).length} categories for file upload context`);
        } else {
          console.log('No documents found in Firestore for file upload context');
        }
      } catch (docError) {
        console.error('Error fetching documents from Firestore for file upload:', docError);
        // Continue without document context if there's an error
      }
    }
    
    // Add company information and document contexts to the message
    if (companyInfo) {
      const companyContext = getCompanyContext(companyInfo);
      
      // Add company context directly to the message 
      if (documentContext) {
        // Add doc context directly to user message with explicit instructions
        fullMessageContent = `${fullMessageContent}\n\n${documentContext}\n\nIMPORTANT: Use the above document context to help analyze the files I've uploaded. These documents contain specific information about our company that should inform your response.`;
      }
    }
    
    // Add the text content to the message
    content.push({
      type: "text",
      text: fullMessageContent
    });
    
    // Add image files directly to the message content if supported
    for (const file of uploadedFiles) {
      const fileType = file.filename ? file.filename.split('.').pop().toLowerCase() : '';
      
      // Only add images directly in the message
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType)) {
        content.push({
          type: "image_file",
          image_file: { file_id: file.id }
        });
      }
    }
    
    console.log('Final structured message content:', content[0].text.substring(0, 200) + '...');
    
    // Add the message to the thread with file attachments
    const message = await openai.beta.threads.messages.create(
      threadId,
      {
        role: 'user',
        content: content,
        attachments: fileIds.map(fileId => ({
          file_id: fileId,
          tools: [{ type: "file_search" }]
        }))
      }
    );
    
    return {
      message,
      fileIds: fileIds
    };
  } catch (error) {
    console.error('Error adding message with files:', error);
    throw error;
  }
}

// Run assistant after uploading files
export async function sendMessageWithFilesAndStream(threadId, message, files, companyInfo = null, onUpdate) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Adding message with files to thread before streaming');
    
    // Add the message with files to the thread
    await addMessageWithFiles(threadId, message, files, companyInfo);
    
    // Run the assistant with real streaming
    console.log('Starting real streaming with thread ID:', threadId);
    return runAssistantWithRealStreaming(threadId, onUpdate);
  } catch (error) {
    console.error('Error in streaming message with files flow:', error);
    onUpdate({
      role: 'assistant',
      content: [{ 
        type: 'text', 
        text: { value: `Error: ${error.message}` } 
      }],
      timestamp: new Date()
    }, true);
    throw error;
  }
}

// Edit a message in a thread and remove subsequent messages
export async function updateMessageAndTruncateThread(threadId, originalMessage, newContent, messageIndex, companyInfo = null) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Editing message using additional_messages approach:', threadId);
    
    // First, check for any active runs and wait for them to complete
    try {
      const runs = await openai.beta.threads.runs.list(threadId);
      const activeRuns = runs.data.filter(run => 
        ['in_progress', 'queued', 'requires_action'].includes(run.status)
      );
      
      if (activeRuns.length > 0) {
        console.log(`Thread has ${activeRuns.length} active runs. Waiting for completion...`);
        // Wait for each active run to complete (or timeout after 30 seconds)
        const waitPromises = activeRuns.map(async (run) => {
          let attempts = 0;
          const maxAttempts = 10;  // 10 attempts, 3 seconds each = 30 seconds max wait time
          
          while (attempts < maxAttempts) {
            console.log(`Checking run ${run.id}, attempt ${attempts + 1}/${maxAttempts}`);
            const runStatus = await openai.beta.threads.runs.retrieve(threadId, run.id);
            
            if (['completed', 'failed', 'cancelled', 'expired'].includes(runStatus.status)) {
              console.log(`Run ${run.id} completed with status: ${runStatus.status}`);
              return;
            }
            
            // Wait 3 seconds before checking again
            await new Promise(resolve => setTimeout(resolve, 3000));
            attempts++;
          }
          
          // If we reach here, the run didn't complete in time - try to cancel it
          console.log(`Run ${run.id} did not complete in time. Attempting to cancel...`);
          try {
            await openai.beta.threads.runs.cancel(threadId, run.id);
          } catch (cancelError) {
            console.warn(`Could not cancel run ${run.id}:`, cancelError);
          }
        });
        
        await Promise.all(waitPromises);
        console.log("All active runs have been handled");
      }
    } catch (runsError) {
      console.warn("Error checking/waiting for active runs:", runsError);
      // Continue with the edit operation even if we couldn't check for active runs
    }
    
    // Get all messages in the thread
    const messages = await getMessages(threadId);
    
    // Find all messages that come after the one being edited
    // Sort messages by creation time (newest first is the default)
    const sortedMessages = [...messages].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    
    // Count user messages to find the target message and identify which to delete
    let userMsgCount = 0;
    let messagesToDelete = [];
    let shouldDelete = false;
    
    for (const message of sortedMessages) {
      if (message.role === 'user') {
        if (shouldDelete) {
          // This message comes after the edited one, delete it
          messagesToDelete.push(message.id);
        } else if (userMsgCount === messageIndex) {
          // We found the message to edit, start deleting all messages after this
          shouldDelete = true;
        }
        userMsgCount++;
      } else if (message.role === 'assistant' && shouldDelete) {
        // Delete assistant responses that come after the edited message
        messagesToDelete.push(message.id);
      }
    }
    
    console.log(`Found ${messagesToDelete.length} messages to delete after the edited message`);
    
    // Delete all messages that come after the edited one
    for (const messageId of messagesToDelete) {
      try {
        await openai.beta.threads.messages.del(threadId, messageId);
        console.log(`Deleted message ${messageId}`);
      } catch (deleteError) {
        console.error(`Error deleting message ${messageId}:`, deleteError);
        // Continue with other deletions even if one fails
      }
    }
    
    // Create a new run with the edited message as an additional message
    const run = await openai.beta.threads.runs.create(
      threadId,
      {
        assistant_id: ASSISTANT_ID,
        additional_messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: newContent
              }
            ]
          }
        ]
      }
    );

    // Return an object with methods to continue the conversation
    return {
      runId: run.id,
      threadId: threadId, // Return the same thread ID
      async continueConversation(onStreamUpdate) {
        // We're already using the run we created, so modify the streaming approach
        // to use the existing run instead of creating a new one
        return streamExistingRun(
          threadId, 
          run.id, 
          (message) => onStreamUpdate(message, false), // Pass non-final updates
          (message) => onStreamUpdate(message, true),  // Pass final message with isFinal=true
          (error) => console.error("Error in stream:", error)
        );
      }
    };
  } catch (error) {
    console.error('Error editing message in thread:', error);
    throw error;
  }
}

// Stream from an existing run by polling for updates
export const streamExistingRun = async (threadId, runId, onMessageUpdate, onFinal = () => {}, onError = () => {}) => {
  console.log(`Streaming existing run: ${runId} for thread: ${threadId}`);
  let attempts = 0;
  const maxAttempts = 30; // Poll for up to 30 seconds
  
  const checkRunStatus = async () => {
    try {
      if (attempts >= maxAttempts) {
        console.warn(`Run polling timed out after ${maxAttempts} attempts`);
        onError(new Error('Run polling timed out'));
        return;
      }
      
      attempts++;
      console.log(`Polling run status (attempt ${attempts}/${maxAttempts})...`);
      
      // Get the current run status
      const run = await openai.beta.threads.runs.retrieve(threadId, runId);
      console.log(`Run status: ${run.status}`);
      
      if (run.status === 'completed') {
        // When the run is complete, fetch the messages
        const messagesResponse = await openai.beta.threads.messages.list(threadId);
        const messages = messagesResponse.data;
        
        // Find the assistant's most recent message
        const latestAssistantMessage = messages
          .filter(msg => msg.role === 'assistant')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        
        if (latestAssistantMessage) {
          // Extract the content and pass to callback
          console.log('Run completed, sending final message');
          onMessageUpdate(latestAssistantMessage);
          onFinal(latestAssistantMessage);
        } else {
          onError(new Error('No assistant message found after completed run'));
        }
        return;
      } else if (['failed', 'cancelled', 'expired'].includes(run.status)) {
        // Handle failed runs
        console.error(`Run ${runId} ${run.status}:`, run.last_error ?? 'No error details');
        onError(new Error(`Run ${run.status}: ${run.last_error?.message || 'Unknown error'}`));
        return;
      } else {
        // For in_progress, queued, and other states, keep polling
        setTimeout(checkRunStatus, 1000); // Poll every second
      }
    } catch (error) {
      console.error('Error polling run status:', error);
      onError(error);
    }
  };
  
  // Start polling
  checkRunStatus();
};

// Helper function to make API calls with consistent error handling
export async function safeApiCall(apiFunction, errorMessage) {
  try {
    return await apiFunction();
  } catch (error) {
    handleError(error, errorMessage);
    throw error;
  }
}

// Get all file IDs from a thread
export async function getAllFileIdsFromThread(threadId) {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }
    
    console.log('Getting all file IDs from thread:', threadId);
    
    // Get all messages from the thread
    const messages = await getMessages(threadId);
    
    // Extract file IDs from all messages
    const fileIds = [];
    
    for (const message of messages) {
      // Check direct file_ids array
      if (message.file_ids && Array.isArray(message.file_ids)) {
        message.file_ids.forEach(fileId => {
          if (!fileIds.includes(fileId)) {
            fileIds.push(fileId);
          }
        });
      }
      
      // Check fileId property
      if (message.fileId && !fileIds.includes(message.fileId)) {
        fileIds.push(message.fileId);
      }
      
      // Check fileIds array
      if (message.fileIds && Array.isArray(message.fileIds)) {
        message.fileIds.forEach(fileId => {
          if (!fileIds.includes(fileId)) {
            fileIds.push(fileId);
          }
        });
      }
      
      // Check content for file references
      if (message.content && Array.isArray(message.content)) {
        for (const item of message.content) {
          // Check for image_file type
          if (item.type === 'image_file' && item.image_file && item.image_file.file_id) {
            const fileId = item.image_file.file_id;
            if (!fileIds.includes(fileId)) {
              fileIds.push(fileId);
            }
          }
          
          // Check for file_attachment type
          if (item.type === 'file_attachment' && item.file_attachment && item.file_attachment.file_id) {
            const fileId = item.file_attachment.file_id;
            if (!fileIds.includes(fileId)) {
              fileIds.push(fileId);
            }
          }
          
          // Check for text annotations with file paths
          if (item.type === 'text' && item.text && item.text.annotations) {
            for (const annotation of item.text.annotations) {
              if (annotation.type === 'file_path' && annotation.file_path && annotation.file_path.file_id) {
                const fileId = annotation.file_path.file_id;
                if (!fileIds.includes(fileId)) {
                  fileIds.push(fileId);
                }
              }
            }
          }
        }
      }
    }
    
    console.log('Found file IDs in thread:', fileIds);
    return fileIds;
  } catch (error) {
    console.error('Error getting file IDs from thread:', error);
    throw error;
  }
}

// Download all files from a thread
export async function downloadAllFilesFromThread(threadId) {
  try {
    // Get all file IDs from the thread
    const fileIds = await getAllFileIdsFromThread(threadId);
    
    if (fileIds.length === 0) {
      console.log('No files found in thread');
      return { success: false, message: 'No files found in thread' };
    }
    
    console.log(`Found ${fileIds.length} files in thread. Starting download...`);
    
    // Download each file
    const downloadResults = [];
    for (let i = 0; i < fileIds.length; i++) {
      const fileId = fileIds[i];
      try {
        console.log(`Downloading file ${i + 1}/${fileIds.length}: ${fileId}`);
        
        // Get file content and metadata
        const fileData = await downloadFileContent(fileId);
        
        // Save file to device
        const success = saveFileToDevice(
          fileData.arrayBuffer, 
          fileData.filename,
          fileData.contentType
        );
        
        downloadResults.push({
          fileId,
          filename: fileData.filename,
          contentType: fileData.contentType,
          success
        });
        
        // Add a small delay to prevent too many simultaneous downloads
        if (i < fileIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Error downloading file ${fileId}:`, error);
        downloadResults.push({
          fileId,
          success: false,
          error: error.message
        });
      }
    }
    
    const successCount = downloadResults.filter(r => r.success).length;
    return {
      success: true,
      message: `Downloaded ${successCount}/${fileIds.length} files`,
      results: downloadResults
    };
  } catch (error) {
    console.error('Error downloading all files from thread:', error);
    return { success: false, message: error.message };
  }
}